import { getPath } from '@/router/utils';
import {
  ROUTE_PUBLIC_NARRATIVE_CREATE,
  ROUTE_PUBLIC_NARRATIVE_EDIT,
} from '@/constants/routes';
import { useTracking } from 'react-tracking';
import { usePathname } from '@/hooks/location/usePathname';
import { useCallback } from 'react';
import { useNarrativeValuationCompany } from '../useNarrativeValuationCompany';
import { useUser } from '@/hooks/useUser';
import { push } from 'redux-first-router';
import { NARRATIVE_USER_POSITION } from '../../utils/schema';

type Props = {
  companyId: string;
  returnPath?: string;
  userHoldsPosition?: boolean;
  uniqueSymbol?: string;
  isUserNarrative?: boolean;
};

const useCreateNarrative = ({
  companyId,
  returnPath,
  uniqueSymbol,
  isUserNarrative = true,
  userHoldsPosition,
}: Props) => {
  const { data: user } = useUser();
  const { trackEvent } = useTracking();
  const pathname = usePathname();

  const { data: companyResponse } = useNarrativeValuationCompany(
    {
      companyId: companyId!,
    },
    {
      enabled: Boolean(companyId && user?.loggedIn),
    }
  );

  const company = companyResponse?.Company;

  const { myNarrative } = company ?? {};

  const userNarrativeId = myNarrative?.id;

  const handleCreateNarrative = useCallback(async () => {
    trackEvent({
      action: 'click',
      target: 'create-narrative',
      modifier: 'create',
      companyId,
      ...(uniqueSymbol ? { uniqueSymbol } : {}),
      ...(isUserNarrative ? { valuationType: 'user_defined' } : {}),
      ...(userHoldsPosition ? { position: NARRATIVE_USER_POSITION.HOLDS } : {}),
    });

    if (!userNarrativeId) {
      push(
        getPath({
          type: ROUTE_PUBLIC_NARRATIVE_CREATE,
          payload: {
            query: { company: companyId, returnPath: returnPath ?? pathname },
          },
        })
      );
    } else {
      push(
        getPath({
          type: ROUTE_PUBLIC_NARRATIVE_EDIT,
          payload: { id: userNarrativeId },
          query: { company: companyId, returnPath: returnPath ?? pathname },
        })
      );
    }
  }, [
    companyId,
    isUserNarrative,
    pathname,
    returnPath,
    trackEvent,
    uniqueSymbol,
    userHoldsPosition,
    userNarrativeId,
  ]);

  return { handleCreateNarrative };
};

export { useCreateNarrative };
